import type { HtmlParserPlugin } from '~/plugins/dom-parser'
import { ELEMENT_NODE, TEXT_NODE } from '#shared/utils/dom'
import { decodeEscapedAnchorMarkup, hasEscapedAnchorMarkup, mergeLabelIntoHeading, normalizeJatsWhitespace } from '#shared/utils/jats-heading'
import { normalizeJuridikaImageUrl } from '~/utils/solr/juridika-media-image'

// =============================================================================
// CONSTANTS
// =============================================================================

const DATA_FOOTNOTE_ID = 'data-footnote-id'
const FOOTNOTE_CONTENT_CLASS = 'footnote__content'
const FOOTNOTE_BUTTON_CLASSES = 'footnote__btn'
const JATS_GRAPHIC_FALLBACK_CLASS = 'jats-graphic-fallback'
const JATS_GRAPHIC_FALLBACK_LABEL = 'Illustrasjon utilgjengelig'
const JATS_GRAPHIC_IMAGE_CLASS = 'jats-graphic-image'
const JATS_FIGURE_LABEL_CLASS = 'jats-figure-label'

const JATS_ELEMENTS = {
  FOOTNOTE: 'fn',
  FOOTNOTE_REF: 'xref',
  LABEL: 'label',
  SUPERSCRIPT: 'sup',
  ITALIC: 'italic',
  TITLE: 'title',
  SECTION: 'sec',
  EXTERNAL_LINK: 'ext-link',
  LIST: 'list',
  LIST_ITEM: 'list-item',
  FIGURE: 'fig',
  GRAPHIC: 'graphic',
  CAPTION: 'caption',
  ALT_TEXT: 'alt-text',
} as const

const HTML_ELEMENTS = {
  PARAGRAPH: 'P',
  DIV: 'DIV',
  SECTION: 'SECTION',
  SPAN: 'span',
  BUTTON: 'button',
  A: 'a',
  I: 'i',
  H3: 'h3',
  OL: 'ol',
  UL: 'ul',
  LI: 'li',
  FIGURE: 'figure',
  IMG: 'img',
  FIGCAPTION: 'figcaption',
} as const

const BLOCK_ELEMENTS = ['P', 'DIV', 'SECTION', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LABEL'] as const
const HEADING_ELEMENTS = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'] as const

// =============================================================================
// TYPES
// =============================================================================

interface FootnoteData {
  id: string
  supElement: Element
  xref: Element
}

interface FootnoteContext {
  sup: Element
  footnoteId: string
  footnoteContent: Element
}

// =============================================================================
// DOM UTILITIES - Type Guards
// =============================================================================

function isElement(node: Node): node is Element {
  return node.nodeType === ELEMENT_NODE
}

function isTextNode(node: Node): node is Text {
  return node.nodeType === TEXT_NODE
}

function isNonEmptyTextNode(node: Node): boolean {
  return isTextNode(node) && !!node.textContent?.trim()
}

function isBlockElement(element: Element): boolean {
  return BLOCK_ELEMENTS.includes(element.nodeName as typeof BLOCK_ELEMENTS[number])
}

function isContainer(element: Element): boolean {
  return element.nodeName === HTML_ELEMENTS.SECTION || element.nodeName === HTML_ELEMENTS.DIV
}

function isFootnoteContent(node: Node): boolean {
  return isElement(node) && node.classList?.contains(FOOTNOTE_CONTENT_CLASS)
}

// =============================================================================
// DOM UTILITIES - Node Manipulation
// =============================================================================

function hasContent(element: Element): boolean {
  return (element.textContent?.trim().length ?? 0) > 0
}

function moveChildrenTo(source: Element, target: Element): void {
  while (source.firstChild) {
    target.appendChild(source.firstChild)
  }
}

function findPreviousSibling(node: Node, predicate: (el: Element) => boolean): Element | null {
  let sibling = node.previousSibling
  while (sibling) {
    if (isElement(sibling) && predicate(sibling)) {
      return sibling
    }
    sibling = sibling.previousSibling
  }
  return null
}

function getPreviousTextNode(node: Node): Text | null {
  const prev = node.previousSibling
  return prev && isTextNode(prev) ? prev : null
}

// =============================================================================
// DOM UTILITIES - Element Replacement
// =============================================================================

function replaceElementType(doc: Document, fromTag: string, toTag: string): void {
  doc.querySelectorAll(fromTag).forEach((element) => {
    const newElement = doc.createElement(toTag)
    moveChildrenTo(element, newElement)
    element.replaceWith(newElement)
  })
}

// =============================================================================
// MAIN PIPELINE
// =============================================================================

/**
 * Normalizes JATS XML format to HTML for proper display
 */
export const normalizeJats: HtmlParserPlugin = (doc) => {
  // Phase 1: Cleanup and normalization
  removeEmptyParagraphs(doc)
  moveContentIntoFnElements(doc)

  // Phase 2: Transform JATS to HTML
  transformJatsFootnotesToHtml(doc)
  convertJatsElementsToHtml(doc)
  mergeSectionLabelsIntoHeadings(doc)
  decodeEscapedAnchorsInHeadings(doc)

  // Phase 3: Content consolidation
  consolidateLooseNodes(doc)
  consolidateListItems(doc)

  // Phase 4: Footnote positioning
  flattenFootnoteContent(doc)
  forEachFootnote(doc, ensureFootnoteIsAdjacent)
  forEachFootnote(doc, normalizeFootnotePunctuation)

  return doc
}

function decodeEscapedAnchorsInHeadings(doc: Document): void {
  doc.querySelectorAll(HEADING_ELEMENTS.map(tag => tag.toLowerCase()).join(',')).forEach((heading) => {
    const currentHtml = heading.innerHTML
    if (!hasEscapedAnchorMarkup(currentHtml)) {
      return
    }

    heading.innerHTML = decodeEscapedAnchorMarkup(currentHtml)

    heading.querySelectorAll('a').forEach((anchor) => {
      const href = anchor.getAttribute('href')?.trim().toLowerCase() ?? ''
      if (href.startsWith('javascript:')) {
        anchor.removeAttribute('href')
      }

      Array.from(anchor.attributes).forEach((attr) => {
        if (attr.name.toLowerCase().startsWith('on')) {
          anchor.removeAttribute(attr.name)
        }
      })
    })
  })
}

// =============================================================================
// FOOTNOTE UTILITIES
// =============================================================================

/**
 * Process each footnote with its context (sup element and content)
 * Eliminates repeated pattern of getting footnote ID and content
 */
function forEachFootnote(doc: Document, callback: (ctx: FootnoteContext, doc: Document) => void): void {
  doc.querySelectorAll(`sup[${DATA_FOOTNOTE_ID}]`).forEach((sup) => {
    const footnoteId = sup.getAttribute(DATA_FOOTNOTE_ID)
    if (!footnoteId) {
      return
    }

    const footnoteContent = doc.getElementById(footnoteId)
    if (!footnoteContent || !footnoteContent.classList.contains(FOOTNOTE_CONTENT_CLASS)) {
      return
    }

    callback({ sup, footnoteId, footnoteContent }, doc)
  })
}

// =============================================================================
// PHASE 1: CLEANUP AND NORMALIZATION
// =============================================================================

function removeEmptyParagraphs(doc: Document): void {
  doc.querySelectorAll(HTML_ELEMENTS.PARAGRAPH).forEach((p) => {
    if (!hasContent(p)) {
      p.remove()
    }
  })
}

function moveContentIntoFnElements(doc: Document): void {
  doc.querySelectorAll(JATS_ELEMENTS.FOOTNOTE).forEach((fn) => {
    fn.querySelector(JATS_ELEMENTS.LABEL)?.remove()

    if (hasContent(fn)) {
      return
    }

    const misplacedContent = fn.parentElement?.nextElementSibling ?? fn.nextElementSibling
    if (misplacedContent) {
      moveChildrenTo(misplacedContent, fn)
      misplacedContent.remove()
    }
  })
}

// =============================================================================
// PHASE 2: JATS TO HTML TRANSFORMATION
// =============================================================================

function transformJatsFootnotesToHtml(doc: Document): void {
  const fnMap = buildFootnoteMap(doc)

  doc.querySelectorAll<Element>(`${JATS_ELEMENTS.FOOTNOTE_REF}[ref-type="fn"][rid]`).forEach((xref) => {
    processFootnoteReference(doc, xref, fnMap)
  })

  fnMap.forEach(fn => fn.remove())
}

function buildFootnoteMap(doc: Document): Map<string, Element> {
  const map = new Map<string, Element>()
  doc.querySelectorAll(`${JATS_ELEMENTS.FOOTNOTE}[id]`).forEach((fn) => {
    const id = fn.getAttribute('id')
    if (id) {
      map.set(id, fn)
    }
  })
  return map
}

function processFootnoteReference(doc: Document, xref: Element, fnMap: Map<string, Element>): void {
  const footnoteData = extractFootnoteData(xref)
  if (!footnoteData) {
    return
  }

  const { id, supElement } = footnoteData
  const footnoteNumber = supElement.textContent || ''

  createFootnoteButton(doc, supElement, id)

  const footnoteElement = fnMap.get(id)
  if (!footnoteElement) {
    xref.replaceWith(supElement)
    return
  }

  const fragment = doc.createDocumentFragment()
  fragment.appendChild(supElement)
  fragment.appendChild(createFootnoteWrapper(doc, id, footnoteNumber, footnoteElement))
  xref.replaceWith(fragment)
}

function extractFootnoteData(xref: Element): FootnoteData | null {
  const id = xref.getAttribute('rid')
  const supElement = xref.querySelector('sup')
  if (!id || !supElement) {
    return null
  }

  supElement.setAttribute(DATA_FOOTNOTE_ID, id)
  return { id, supElement, xref }
}

function createFootnoteButton(doc: Document, supElement: Element, footnoteId: string): void {
  const button = doc.createElement(HTML_ELEMENTS.BUTTON)
  button.className = FOOTNOTE_BUTTON_CLASSES
  button.setAttribute(DATA_FOOTNOTE_ID, footnoteId)
  button.setAttribute('aria-expanded', 'false')
  button.setAttribute('aria-controls', footnoteId)
  button.textContent = supElement.textContent || ''

  supElement.textContent = ''
  supElement.appendChild(button)
}

function createFootnoteWrapper(doc: Document, footnoteId: string, footnoteNumber: string, footnoteElement: Element): Element {
  const wrapper = doc.createElement(HTML_ELEMENTS.SPAN)
  wrapper.id = footnoteId
  wrapper.className = FOOTNOTE_CONTENT_CLASS
  wrapper.setAttribute(DATA_FOOTNOTE_ID, footnoteId)
  wrapper.setAttribute('hidden', 'true')

  const header = createFootnoteHeader(doc, {
    tag: HTML_ELEMENTS.SPAN,
    id: footnoteId,
    labelContent: footnoteNumber,
  })
  wrapper.appendChild(header)

  const contentContainer = doc.createElement(HTML_ELEMENTS.SPAN)
  contentContainer.className = 'footnote__text'
  contentContainer.style = 'word-break: break-word;'
  wrapper.appendChild(contentContainer)
  moveChildrenTo(footnoteElement, contentContainer)

  return wrapper
}

function convertJatsElementsToHtml(doc: Document): void {
  replaceElementType(doc, JATS_ELEMENTS.ITALIC, HTML_ELEMENTS.I)
  replaceElementType(doc, JATS_ELEMENTS.TITLE, HTML_ELEMENTS.H3)
  replaceElementType(doc, JATS_ELEMENTS.SECTION, HTML_ELEMENTS.SECTION)
  replaceElementType(doc, JATS_ELEMENTS.LIST_ITEM, HTML_ELEMENTS.LI)
  convertFigures(doc)
  convertExternalLinks(doc)
  convertLists(doc)
}

function convertFigures(doc: Document): void {
  replaceElementType(doc, JATS_ELEMENTS.FIGURE, HTML_ELEMENTS.FIGURE)

  doc.querySelectorAll(JATS_ELEMENTS.GRAPHIC).forEach((graphic) => {
    const rawSrc = graphic.getAttribute('xlink:href') || graphic.getAttribute('href')
    const normalizedSrc = rawSrc ? normalizeJuridikaImageUrl(rawSrc) : undefined
    const figure = graphic.closest(HTML_ELEMENTS.FIGURE)
    const figureAltText = figure
      ? Array.from(figure.children).find(child => child.tagName === JATS_ELEMENTS.ALT_TEXT.toUpperCase())?.textContent
      : undefined
    const altText = (graphic.querySelector(JATS_ELEMENTS.ALT_TEXT)?.textContent || figureAltText)?.replace(/\s+/g, ' ').trim()

    if (typeof normalizedSrc !== 'string') {
      const fallback = doc.createElement(HTML_ELEMENTS.DIV)
      fallback.className = JATS_GRAPHIC_FALLBACK_CLASS
      fallback.setAttribute('role', 'img')
      fallback.setAttribute('aria-label', altText || JATS_GRAPHIC_FALLBACK_LABEL)

      graphic.replaceWith(fallback)
      return
    }

    const img = doc.createElement(HTML_ELEMENTS.IMG)
    const resolvedSrc = normalizedSrc
    img.setAttribute('src', resolvedSrc)
    img.setAttribute('alt', altText || '')
    img.setAttribute('loading', 'lazy')
    img.className = JATS_GRAPHIC_IMAGE_CLASS

    graphic.replaceWith(img)
  })

  doc.querySelectorAll(HTML_ELEMENTS.FIGURE).forEach((figure) => {
    Array.from(figure.children)
      .filter(child => child.tagName === JATS_ELEMENTS.ALT_TEXT.toUpperCase())
      .forEach(child => child.remove())

    const captionContainer = Array.from(figure.children).find(child => child.tagName === JATS_ELEMENTS.CAPTION.toUpperCase())

    const captionElements = captionContainer
      ? Array.from(captionContainer.children)
      : Array.from(figure.children).filter(child =>
          child.tagName === JATS_ELEMENTS.LABEL.toUpperCase() || child.tagName === HTML_ELEMENTS.PARAGRAPH,
        )

    if (captionElements.length === 0) {
      return
    }

    const figcaption = doc.createElement(HTML_ELEMENTS.FIGCAPTION)
    captionElements.forEach((element) => {
      if (element.tagName === JATS_ELEMENTS.LABEL.toUpperCase()) {
        const captionLabel = doc.createElement(HTML_ELEMENTS.SPAN)
        captionLabel.className = JATS_FIGURE_LABEL_CLASS
        moveChildrenTo(element, captionLabel)
        element.remove()
        figcaption.appendChild(captionLabel)
        return
      }

      figcaption.appendChild(element)
    })

    captionContainer?.remove()
    figure.appendChild(figcaption)
  })
}

function convertExternalLinks(doc: Document): void {
  doc.querySelectorAll(JATS_ELEMENTS.EXTERNAL_LINK).forEach((extLink) => {
    const anchor = doc.createElement(HTML_ELEMENTS.A)
    const href = extLink.getAttribute('xlink:href') || extLink.getAttribute('href')

    if (href) {
      anchor.href = href
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
    }

    moveChildrenTo(extLink, anchor)
    extLink.replaceWith(anchor)
  })
}

function convertLists(doc: Document): void {
  doc.querySelectorAll(JATS_ELEMENTS.LIST).forEach((list) => {
    const listType = list.getAttribute('list-type')
    const htmlList = doc.createElement(listType === 'order' ? HTML_ELEMENTS.OL : HTML_ELEMENTS.UL)
    moveChildrenTo(list, htmlList)
    list.replaceWith(htmlList)
  })
}

function mergeSectionLabelsIntoHeadings(doc: Document): void {
  doc.querySelectorAll(JATS_ELEMENTS.LABEL).forEach((label) => {
    const labelText = normalizeJatsWhitespace(label.textContent ?? '')
    if (!labelText) {
      label.remove()
      return
    }

    let node: Node | null = label.nextSibling
    while (node && isTextNode(node) && !node.textContent?.trim()) {
      const next = node.nextSibling
      node.remove()
      node = next
    }

    if (!node || !isElement(node) || !HEADING_ELEMENTS.includes(node.nodeName as typeof HEADING_ELEMENTS[number])) {
      return
    }

    const headingText = normalizeJatsWhitespace(node.textContent ?? '')
    const mergedHeadingText = mergeLabelIntoHeading(labelText, headingText)
    if (mergedHeadingText !== headingText) {
      node.insertBefore(doc.createTextNode(`${labelText} `), node.firstChild)
    }

    label.remove()
  })
}

// =============================================================================
// PHASE 3: CONTENT CONSOLIDATION
// =============================================================================

function consolidateLooseNodes(doc: Document): void {
  processContainerForLooseNodes(doc.body)
}

function processContainerForLooseNodes(container: Node): void {
  Array.from(container.childNodes).forEach((node) => {
    if (!isElement(node)) {
      if (isNonEmptyTextNode(node)) {
        appendToPreviousParagraph(node)
      }
      return
    }

    if (isFootnoteContent(node)) {
      return
    }
    if (isContainer(node)) {
      processContainerForLooseNodes(node)
      return
    }
    if (isBlockElement(node)) {
      return
    }

    appendToPreviousParagraph(node)
  })
}

function appendToPreviousParagraph(node: Node): void {
  const paragraph = findPreviousSibling(node, el => el.nodeName === HTML_ELEMENTS.PARAGRAPH)
  if (paragraph) {
    paragraph.appendChild(node)
  }
}

function consolidateListItems(doc: Document): void {
  doc.querySelectorAll('li').forEach((li) => {
    const children = Array.from(li.childNodes)
    const targetParagraph = children.find(node =>
      isElement(node) && node.nodeName === HTML_ELEMENTS.PARAGRAPH,
    ) as Element | undefined

    if (!targetParagraph) {
      return
    }

    children.forEach((node) => {
      if (node === targetParagraph) {
        return
      }
      if (isFootnoteContent(node)) {
        return
      }

      const shouldConsolidate = isNonEmptyTextNode(node) || (isElement(node) && !isBlockElement(node))
      if (shouldConsolidate) {
        targetParagraph.appendChild(node)
      }
    })
  })
}

// =============================================================================
// PHASE 4: FOOTNOTE POSITIONING
// =============================================================================

function flattenFootnoteContent(doc: Document): void {
  doc.querySelectorAll(`.${FOOTNOTE_CONTENT_CLASS}`).forEach((footnote) => {
    footnote.querySelectorAll(HTML_ELEMENTS.PARAGRAPH).forEach((p) => {
      const span = doc.createElement(HTML_ELEMENTS.SPAN)
      moveChildrenTo(p, span)
      p.replaceWith(span)
    })
  })
}

function ensureFootnoteIsAdjacent({ sup, footnoteId, footnoteContent }: FootnoteContext): void {
  const nextSibling = sup.nextSibling
  const isAdjacent = nextSibling
    && isElement(nextSibling)
    && nextSibling.classList?.contains(FOOTNOTE_CONTENT_CLASS)
    && nextSibling.id === footnoteId

  if (!isAdjacent) {
    sup.parentNode?.insertBefore(footnoteContent, sup.nextSibling)
  }
}

function normalizeFootnotePunctuation({ sup, footnoteContent }: FootnoteContext, doc: Document): void {
  const textNode = getPreviousTextNode(sup)
  if (!textNode) {
    return
  }

  const text = textNode.textContent || ''
  const punctuationMatch = text.match(/([,;])\s*$/)

  if (punctuationMatch && punctuationMatch[1]) {
    // Move comma/semicolon after footnote
    textNode.textContent = text.replace(/([,;])\s*$/, '')
    const punctuationNode = doc.createTextNode(punctuationMatch[1])
    footnoteContent.parentNode?.insertBefore(punctuationNode, footnoteContent.nextSibling)
  }
  else {
    // Remove trailing whitespace to prevent gaps
    const trimmed = text.replace(/\s+$/, '')
    if (trimmed !== text) {
      textNode.textContent = trimmed
    }
  }
}
